import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { ClinicsManagementActions } from 'application/constants/actions';
import { ClinicsManagementAPI } from 'integration/api/clinicsManagement';
import {
  GetClinicsParams,
  UpdateClinicManagementData,
} from 'common/types/clinicsManagement';
/**
 * Asynchronously fetches a list of clinics based on the given parameters.
 *
 * @param params - An object containing search and filter parameters for fetching the clinics.
 * @returns The response data containing the fetched clinics.
 */
export const getClinics = createAsyncThunk(
  ClinicsManagementActions.getClinics,
  async (params: GetClinicsParams, { rejectWithValue }) => {
    try {
      const { data } = await ClinicsManagementAPI.getClinics(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously fetches a list of clinic suggestions based on the given search term.
 *
 * @param search - The search term to use for fetching the clinic suggestions.
 * @returns The response data containing the fetched clinic suggestions.
 */
export const getClinicsSuggestions = createAsyncThunk(
  ClinicsManagementActions.getClinicsSuggestions,
  async (search: string, { rejectWithValue }) => {
    try {
      const { data } = await ClinicsManagementAPI.getClinicsSuggestions(search);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously fetches a single clinic based on its ID.
 *
 * @param clinicId - The ID of the clinic to fetch.
 * @returns The response data containing the fetched clinic.
 */
export const getClinicById = createAsyncThunk(
  ClinicsManagementActions.getClinicById,
  async (clinicId: string, { rejectWithValue }) => {
    try {
      const { data } = await ClinicsManagementAPI.getClinicById(clinicId);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously updates a clinic based on its ID and the provided update data.
 *
 * @param data - An object containing the updated data for the clinic.
 * @returns The response data containing the updated clinic.
 */
export const updateClinic = createAsyncThunk(
  ClinicsManagementActions.updateClinic,
  async (data: UpdateClinicManagementData, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ClinicsManagementAPI.updateClinic(
        data,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously deletes a clinic based on its ID.
 *
 * @param clinicId - The ID of the clinic to delete.
 * @returns The response data containing the deleted clinic.
 */
export const deleteClinic = createAsyncThunk(
  ClinicsManagementActions.deleteClinic,
  async (clinicId: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await ClinicsManagementAPI.deleteClinic(
        clinicId,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
